/**
 * CommentSection Component - Unified container for comments
 * Includes header with add button, comments list, and add/edit input
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { CommentSectionProps } from './types';
import CommentCard from './CommentCard';
import AddCommentInput from './AddCommentInput';

// ============================================================
// SVG Icon Components
// ============================================================

const MessageCirclePlusIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7.9 20A9 9 0 1 0 4 16.1L2 22l5.9-2z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 12h8M12 8v8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// CommentSection Component
// ============================================================

const CommentSection: React.FC<CommentSectionProps> = ({
  title,
  comments,
  showAddComment,
  editingComment,
  hideAddButton = false,
  onAddClick,
  onEditComment,
  onDeleteComment,
  onSubmitComment,
  onCancelComment,
}) => {
  const { colors } = useTheme();
  const { t } = useLanguage();

  // Use provided title or default to translated title
  const sectionTitle = title || t('comments.title');

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
  };

  return (
    <View style={[styles.notesCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
      {/* Header row with title and add button - matches Figma node 2462:154177 */}
      <View style={styles.notesHeader}>
        {/* Hide add button for view-only users (executive) */}
        {!hideAddButton && (
          <TouchableOpacity
            style={styles.notesAddButton}
            onPress={onAddClick}
          >
            <MessageCirclePlusIcon size={15} color={themeColors.iconDefault} />
          </TouchableOpacity>
        )}
        <View style={styles.notesTitleContainer}>
          <Text style={[styles.notesTitle, { color: themeColors.textPrimary }]}>{sectionTitle}</Text>
        </View>
      </View>

      {/* Comments list - matches Figma node 2462:154194 */}
      <View style={styles.commentsList}>
        {comments.length === 0 && !showAddComment ? (
          <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('comments.noComments')}</Text>
        ) : (
          comments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              onEdit={onEditComment}
              onDelete={onDeleteComment}
            />
          ))
        )}

        {/* Add Comment Input */}
        {showAddComment && (
          <AddCommentInput
            onSubmit={onSubmitComment}
            onCancel={onCancelComment}
            editingComment={editingComment}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Notes Card - matches Figma node 2462:154176 (ArCard10)
  notesCard: {
    backgroundColor: '#FFFFFF', // general/background
    borderRadius: 8, // semantic/rounded-lg
    padding: 12, // absolute/3
    borderWidth: 1,
    borderColor: '#E2E8F0', // general/border
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 16, // absolute/4 - gap between header and comments
  },
  // Header - matches Figma node 2462:154177
  notesHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24, // gap between button and title
  },
  // Add button - matches Figma node 2462:348270 (Toggle Icon Button)
  notesAddButton: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    borderRadius: 8, // semantic/rounded-lg
    backgroundColor: 'rgba(51, 65, 85, 0.05)', // unofficial/outline-active
    borderWidth: 1,
    borderColor: '#E2E8F0', // general/border
    justifyContent: 'center',
    alignItems: 'center',
    padding: 7,
  },
  // Title container - matches Figma node 2462:154178
  notesTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  // Title text - matches Figma node 2462:154184
  notesTitle: {
    fontSize: 14, // paragraph/paragraph small font size
    fontFamily: FONTS.regular,
    color: '#414042', // general/foreground
    textAlign: 'right',
    lineHeight: 20, // 1.4 line height
  },
  // Comments list - matches Figma node 2462:154194
  commentsList: {
    gap: 8, // gap between comments
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B', // general/muted-foreground
    textAlign: 'center',
    paddingVertical: 20,
  },
});

export default CommentSection;
